import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle, XCircle, Minus } from 'lucide-react';
import clsx from 'clsx';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import CloseButton from '@components/common/CloseButton';
import WarningNotice from '@components/common/WarningNotice';
import { Textarea } from '@components/common';
import { AssessmentItem, ItemEvaluation, PROCESS_TYPE_LABELS } from '@app-types';
import { assessmentService } from '@services';
import { LoadingSpinner } from '@components/common/loading';
import { getPrefillEvaluation, stripEvaluationNoteStamp } from '@utils/assessmentUtils';
import { processTypeToVariant } from '@utils/badgeMappers';
import styles from './ProcessEvaluationModal.module.css';

interface ProcessEvaluationModalProps {
  item: AssessmentItem;
  onClose: () => void;
  onSaved: () => void;
}

const ProcessEvaluationModal: React.FC<ProcessEvaluationModalProps> = ({ item, onClose, onSaved }) => {
  // Start from the saved evaluation so re-saving does not silently wipe earlier notes (ADO #1113)
  const [selectedEvaluation, setSelectedEvaluation] = useState<ItemEvaluation | ''>(() =>
    getPrefillEvaluation(item.evaluation)
  );
  const [notes, setNotes] = useState(() => stripEvaluationNoteStamp(item.notes));
  const queryClient = useQueryClient();

  // Evaluation options with descriptions
  const evaluationOptions = [
    {
      value: 'Implemented' as ItemEvaluation,
      label: 'Implemented',
      description: 'Process is fully implemented and operational',
      icon: CheckCircle,
      color: '#10b981',
      bgColor: '#ecfdf5'
    },
    {
      value: 'Gap' as ItemEvaluation,
      label: 'Gap',
      description: 'Process has deficiencies or is not implemented',
      icon: XCircle,
      color: '#ef4444',
      bgColor: '#fef2f2'
    },
    {
      value: 'Not Applicable' as ItemEvaluation,
      label: 'Not Applicable',
      description: 'Process is not relevant for this supplier/contract',
      icon: Minus,
      color: '#6b7280',
      bgColor: '#f9fafb'
    }
  ];

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: { evaluation: ItemEvaluation; notes: string }) =>
      assessmentService.updateAssessmentItem(item.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessment-items', item.assessment_id] });
      queryClient.invalidateQueries({ queryKey: ['assessment', item.assessment_id] });
      queryClient.invalidateQueries({ queryKey: ['audit-history', 'assessment', item.assessment_id] });
      onSaved();
      onClose();
    },
    onError: (error) => {
      console.error('Error updating assessment item:', error);
    }
  });

  const handleSubmit = () => {
    if (!selectedEvaluation) {
      alert('Please select an evaluation status');
      return;
    }

    // Notes required ONLY for Not Applicable
    if (selectedEvaluation === 'Not Applicable' && !notes.trim()) {
      alert('Justification is required when marking a process as Not Applicable.');
      return;
    }

    updateMutation.mutate({
      evaluation: selectedEvaluation,
      notes: notes.trim()
    });
  };

  const handleEvaluationSelect = (evaluation: ItemEvaluation) => {
    setSelectedEvaluation(evaluation);
  };

  return (
    <div className={styles.evaluationModalOverlay}>
      <div className={styles.evaluationModal} data-test-id="process-evaluation-modal">
        {/* Loading Overlay */}
        {updateMutation.isPending && (
          <div className={styles.evaluationLoadingOverlay}>
            <div className={styles.evaluationLoadingSpinner}>
              <LoadingSpinner size="medium" />
              <p>Saving evaluation...</p>
            </div>
          </div>
        )}

        <div className={styles.evaluationHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerIcon}>
              <AlertCircle size={24} color="#3b82f6" />
            </div>
            <div className={styles.headerText}>
              <h3>Evaluate Process</h3>
              <p>
                You are about to evaluate this assessment process. Please select a status and provide mandatory notes.
              </p>
            </div>
          </div>
          <CloseButton onClick={onClose} disabled={updateMutation.isPending} />
        </div>

        <div className={styles.evaluationContent}>
          {/* Process Information */}
          <div className={styles.processInfo}>
            <h4>{item.process_name}</h4>
            <p>{item.process_description}</p>
            <div className={styles.processMeta}>
              <span className={styles.domainName}>{item.domain_name}</span>
              <Badge
                variant={processTypeToVariant(item.process_type)}
                title={PROCESS_TYPE_LABELS[item.process_type]}
                ariaLabel={PROCESS_TYPE_LABELS[item.process_type]}
                dataTestId="process-type-badge"
              >
                {item.process_type}
              </Badge>
              <span
                className={clsx(styles.priorityBadge, {
                  [styles.priorityHigh]: item.process_priority.toLowerCase() === 'high',
                  [styles.priorityMedium]: item.process_priority.toLowerCase() === 'medium',
                  [styles.priorityLow]: item.process_priority.toLowerCase() === 'low'
                })}
              >
                {item.process_priority} Priority
              </span>
            </div>
          </div>

          {/* Evaluation Options */}
          <div className={styles.evaluationOptions}>
            <h5>Select Evaluation Status</h5>
            <div className={styles.optionsGrid}>
              {evaluationOptions.map((option) => {
                const IconComponent = option.icon;
                return (
                  <button
                    type="button"
                    key={option.value}
                    className={clsx(styles.evaluationOption, {
                      [styles.selected]: selectedEvaluation === option.value
                    })}
                    onClick={() => !updateMutation.isPending && handleEvaluationSelect(option.value)}
                    style={{
                      borderColor: selectedEvaluation === option.value ? option.color : '#e5e7eb',
                      backgroundColor: selectedEvaluation === option.value ? option.bgColor : '#ffffff'
                    }}
                  >
                    <div className={styles.optionHeader}>
                      <IconComponent size={20} color={option.color} />
                      <span className={styles.optionLabel}>{option.label}</span>
                    </div>
                    <p className={styles.optionDescription}>{option.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mandatory Notes */}
          <div className={styles.notesSection}>
            <Textarea
              id="evaluation-notes"
              label={
                <>
                  <h5>
                    Evaluation Notes <span className={styles.required}>*</span>
                  </h5>
                  <p>Please provide details about your evaluation. Notes are mandatory.</p>
                </>
              }
              placeholder="Explain your evaluation decision, provide evidence details, or note any specific observations..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              dataTestId="evaluation-notes"
            />
            <div className={styles.notesFooter}>
              <span
                className={clsx(styles.charCount, {
                  [styles.warning]: notes.length < 10
                })}
              >
                {notes.length} characters (minimum 10 recommended)
              </span>
            </div>
          </div>

          {/* Warning Notice */}
          <WarningNotice icon={<AlertCircle size={16} />}>
            <span>
              This evaluation will update the assessment status and cannot be easily undone. Please ensure your
              evaluation is accurate.
            </span>
          </WarningNotice>
        </div>

        <div className={styles.evaluationFooter}>
          <Button variant="ghost" onClick={onClose} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!selectedEvaluation || !notes.trim() || updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving Evaluation...' : 'Save Evaluation'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProcessEvaluationModal;
